import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, atLeast, on, onDay, type KnownProblem, type Role } from "../api";
import { Board, Notice, Row, Section, Status } from "./Look";

const FIELD =
  "max-w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-800";

/** A month ahead, as the date input wants it. Long enough for a decision to be
 *  made, short enough that nobody forgets the problem was ever declared. */
function defaultUntil(now = new Date()): string {
  const d = new Date(now.getTime() + 30 * 86400 * 1000);
  return d.toISOString().slice(0, 10);
}

/** End of the chosen day, local time: "until 28 Oct" includes the 28th. */
export function untilToTs(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59).getTime() / 1000;
}

/** On a failure row: this failure is accounted for, why, and until when. */
export function KnownNote({ k }: { k: KnownProblem }) {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-amber-800 dark:text-amber-300" data-testid="known-note">
      {t("known.chip", { date: onDay(k.expires_at), reason: k.reason })}
      {k.created_by && <span className="text-slate-500"> · {t("known.by", { who: k.created_by })}</span>}
    </p>
  );
}

/** Declare one failing case a known problem: a reason and a date, both needed. */
export function MarkKnown({
  projectId,
  scenario,
  onDone,
}: {
  projectId: number;
  scenario: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState(defaultUntil());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) {
    return (
      <button type="button" className="btn-quiet text-sm" onClick={() => setOpen(true)}>
        {t("known.mark")}
      </button>
    );
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      await api.addKnownProblem(projectId, scenario, reason.trim(), untilToTs(until));
      setOpen(false);
      setReason("");
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-2 text-sm">
      <label className="block">
        <span className="block text-slate-600 dark:text-slate-300">{t("known.reason")}</span>
        <textarea
          className={`${FIELD} w-full`}
          rows={2}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <label className="flex flex-wrap items-center gap-2">
        <span className="text-slate-600 dark:text-slate-300">{t("known.until")}</span>
        <input type="date" className={FIELD} value={until} onChange={(e) => setUntil(e.target.value)} />
      </label>
      {error && <Notice tone="error">{error}</Notice>}
      <div className="flex gap-2">
        <button type="button" className="btn-primary" disabled={busy || !reason.trim() || !until} onClick={save}>
          {t("known.save")}
        </button>
        <button type="button" className="btn-quiet" onClick={() => setOpen(false)}>
          {t("known.cancel")}
        </button>
      </div>
    </div>
  );
}

function StateText({ k }: { k: KnownProblem }) {
  const { t } = useTranslation();
  if (k.state === "open") return <>{t("known.stateOpen", { date: onDay(k.expires_at) })}</>;
  if (k.state === "expired") return <>{t("known.stateExpired", { date: onDay(k.expires_at) })}</>;
  return (
    <>
      {k.closed_why === "passed"
        ? t("known.closedPassed", { date: on(k.closed_at) })
        : t("known.closedRemoved", { date: on(k.closed_at), who: k.closed_by || "?" })}
    </>
  );
}

/** The project's known problems: in force, expired, and recently ended. */
export default function KnownProblems({ projectId, role }: { projectId: number; role: Role | null }) {
  const { t } = useTranslation();
  const [list, setList] = useState<KnownProblem[] | null>(null);
  const [error, setError] = useState("");
  const may = atLeast(role, "maintainer");

  const load = useCallback(async () => {
    try {
      setList((await api.knownProblems(projectId)).known);
    } catch {
      /* a service from before known problems: the section stays away */
    }
  }, [projectId]);
  useEffect(() => {
    void load();
  }, [load]);

  if (list === null) return null;
  async function end(id: number) {
    setError("");
    try {
      await api.removeKnownProblem(id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }
  return (
    <Section card title={t("known.title")}>
      <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">{t("known.intro")}</p>
      {error && <Notice tone="error">{error}</Notice>}
      {list.length === 0 ? (
        <p className="text-base text-slate-500">{t("known.none")}</p>
      ) : (
        <Board>
          {list.map((k) => (
            <Row
              key={k.id}
              status={
                <Status tone={k.state === "open" ? "warn" : "off"}><StateText k={k} /></Status>
              }
              title={<span className="font-mono text-sm">{k.scenario}</span>}
              sub={k.reason}
              aside={
                may && k.state === "open" ? (
                  <button type="button" className="btn-quiet" onClick={() => void end(k.id)}>
                    {t("known.end")}
                  </button>
                ) : k.created_by ? (
                  <span className="text-sm text-slate-500">{t("known.by", { who: k.created_by })}</span>
                ) : undefined
              }
            />
          ))}
        </Board>
      )}
    </Section>
  );
}
